import { useEffect, useRef } from 'react';
import { UploadedFile, StoredFileMetadata } from '../types';
import { DataCollectionAiAgentOperationType } from '../../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels';

/**
 * Helper function to get unique localStorage keys based on request ID and AI operation type
 */
export const getStorageKey = (
    requestId?: string,
    operationType?: DataCollectionAiAgentOperationType | null
): string => {
    const requestSuffix = requestId ? `_${requestId}` : '';
    const operationSuffix = operationType === null || operationType === undefined
        ? ''
        : `_${DataCollectionAiAgentOperationType[operationType]}`;

    return `ai_parser_uploaded_files${requestSuffix}${operationSuffix}`;
};

/**
 * Custom hook for managing file metadata persistence in localStorage
 * 
 * @param requestId - Optional request ID for scoped storage
 * @param operationType - AI operation type for scoped storage
 * @param uploadedFiles - Current uploaded files array
 * @param onUploadedFilesChange - Callback to update uploaded files state
 * @returns Object with clearMetadata method
 */
export const useFileMetadataStorage = (
    requestId: string | undefined,
    operationType: DataCollectionAiAgentOperationType,
    uploadedFiles: UploadedFile[],
    onUploadedFilesChange: (files: UploadedFile[]) => void
) => {
    const hasLoadedFromStorage = useRef(false);
    const previousUploadedFilesLength = useRef(uploadedFiles.length);

    /**
     * Loads file metadata from localStorage and reconstructs FileUploadItem objects
     */
    useEffect(() => {
        // Only load once on mount, and only if there are no files already
        if (hasLoadedFromStorage.current || uploadedFiles.length > 0) {
            return;
        }

        const filesKey = getStorageKey(requestId, operationType);
        const storedData = localStorage.getItem(filesKey);

        if (storedData) {
            try {
                const metadata: StoredFileMetadata[] = JSON.parse(storedData);
                
                // Reconstruct FileUploadItem objects from metadata
                // Note: We can't restore the actual File objects, but we can create placeholder ones
                const restoredFiles: UploadedFile[] = metadata.map(meta => ({
                    id: meta.id,
                    fileId: meta.fileId,
                    file: new File([], meta.fileName, { type: 'application/octet-stream' }),
                    isUploading: false,
                    fileName: meta.fileName,  // Preserve file name
                    fileSize: meta.fileSize   // Preserve file size
                }));

                if (restoredFiles.length > 0) {
                    onUploadedFilesChange(restoredFiles);
                }
            } catch (error) {
                console.error('Failed to load file metadata from localStorage:', error);
            }
        }

        hasLoadedFromStorage.current = true;
    }, [requestId, operationType, uploadedFiles.length, onUploadedFilesChange]);

    /**
     * Creates metadata array from file upload items
     */
    const createFileMetadata = (files: UploadedFile[]): StoredFileMetadata[] => {
        return files
            .filter(item => item.fileId) // Only persist files uploaded to backend
            .map(item => ({
                id: item.id,
                fileId: item.fileId!,
                fileName: item.fileName,
                fileSize: item.fileSize
            }));
    };

    /**
     * Saves file metadata to localStorage
     */
    const saveMetadata = (filesKey: string, metadata: StoredFileMetadata[]) => {
        if (metadata.length > 0) {
            localStorage.setItem(filesKey, JSON.stringify(metadata));
        } else {
            localStorage.removeItem(filesKey);
        }
    };

    /**
     * Clears file metadata from localStorage
     */
    const clearMetadata = (filesKey: string) => {
        localStorage.removeItem(filesKey);
    };

    /**
     * Auto-persist file metadata whenever uploadedFiles changes
     */
    useEffect(() => {
        const filesKey = getStorageKey(requestId, operationType);

        if (uploadedFiles.length > 0) {
            const fileMetadata = createFileMetadata(uploadedFiles);
            saveMetadata(filesKey, fileMetadata);
        } else if (previousUploadedFilesLength.current > 0) {
            clearMetadata(filesKey);
        }

        previousUploadedFilesLength.current = uploadedFiles.length;
    }, [uploadedFiles, requestId, operationType]);

    return {
        clearMetadata: () => clearMetadata(getStorageKey(requestId, operationType))
    };
};
